import { Checkbox } from '@HoudiniUI/Checkbox';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@HoudiniUI/Field';
import { Input } from '@HoudiniUI/Input';
import { TextArea } from '@HoudiniUI/TextArea';
import { css } from 'aphrodite';
import { useEffect, useMemo, useState } from 'react';
import { IAICampaignBrief, IAICampaignQuota, availableForBrief } from '../../../../models/AICampaign';
import { useAICampaignQuota } from '../../../../queries/AICampaign';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { styleSheet } from './styles';

interface IProps {
	brief?: IAICampaignBrief;
	/** Reports the current brief on every edit, or null while it is not yet valid. Navigation lives with the caller. */
	onBriefChange: (brief: IAICampaignBrief | null) => void;
}

export const EditAICampaignBrief: React.FC<IProps> = ({ brief, onBriefChange }) => {
	const [goal, setGoal] = useState(brief?.goal ?? '');
	// Roughly ten times the cost and noticeably slower, so it must be opted into rather than defaulted on.
	const [webSearchEnabled, setWebSearchEnabled] = useState(brief?.webSearchEnabled ?? false);
	const [skipRecentlyContactedDays, setSkipRecentlyContactedDays] = useState(
		brief?.skipRecentlyContactedDays?.toString() ?? ''
	);

	const { data: quota, isLoading: quotaLoading } = useAICampaignQuota();

	const available = useMemo(() => availableForBrief(quota, webSearchEnabled), [quota, webSearchEnabled]);

	const skipDays = Number(skipRecentlyContactedDays.trim());
	const hasSkipDays = skipRecentlyContactedDays.trim().length > 0 && Number.isInteger(skipDays) && skipDays > 0;
	const skipDaysInvalid = skipRecentlyContactedDays.trim().length > 0 && !hasSkipDays;

	const isValid = goal.trim().length > 0 && (available === undefined || available > 0) && !skipDaysInvalid;

	useEffect(() => {
		onBriefChange(
			isValid
				? {
						...brief,
						goal: goal.trim(),
						webSearchEnabled,
						skipRecentlyContactedDays: hasSkipDays ? skipDays : null,
					}
				: null
		);
		// `brief` is only the mount-time seed for the fields; reacting to it would echo our own reports back around.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [goal, webSearchEnabled, hasSkipDays, skipDays, isValid, onBriefChange]);

	return (
		<div className={css(styleSheet.container)}>
			<div>
				<h2 className={css(styleSheet.heading)}>What should these emails accomplish?</h2>
				<p className={css(styleSheet.description)}>
					Describe the goal and every recipient gets their own email, written from what Levitate already knows about
					them. Choose who receives it on the right, and you will pick the send date after that.
				</p>
			</div>

			<FieldGroup>
				<Field>
					<FieldLabel htmlFor='ai-campaign-goal'>What each email should say</FieldLabel>
					<TextArea
						id='ai-campaign-goal'
						onChange={event => setGoal(event.target.value)}
						placeholder={
							'Example: Remind each client that their vehicle is approaching its next scheduled service, ' +
							'referencing the vehicle on file and what was done last visit.'
						}
						styles={[styleSheet.goalTextArea]}
						value={goal}
					/>
					<FieldDescription>
						The purpose of the campaign, in your words. Anything you would tell a colleague writing these by hand
						belongs here. It also decides who is left out: a recipient the goal plainly does not apply to is skipped.
					</FieldDescription>
				</Field>

				{/* Research instructions still exist on the brief API-side, but the form no longer collects them: what
				    belongs there versus in the goal was too confusing to explain, and the goal alone carries it. */}
				<Field>
					<div className={css(styleSheet.researchToggleRow)}>
						<Checkbox
							checked={webSearchEnabled}
							id='ai-campaign-web-search'
							onCheckedChange={checked => setWebSearchEnabled(checked === true)}
						/>
						<div className={css(styleSheet.toggleCopy)}>
							<FieldLabel htmlFor='ai-campaign-web-search'>Also allow searching the web</FieldLabel>
							<FieldDescription>
								Only needed when the emails depend on something Levitate does not already hold. It makes generation
								slower and draws on a separate, smaller monthly allowance, so leave it off for campaigns built from your
								own data such as policy renewals or service history.
							</FieldDescription>
						</div>
					</div>
				</Field>

				<Field>
					<FieldLabel htmlFor='ai-campaign-skip-recent'>Skip anyone contacted in the last (days)</FieldLabel>
					<Input
						id='ai-campaign-skip-recent'
						inputMode='numeric'
						min={1}
						onChange={event => setSkipRecentlyContactedDays(event.target.value)}
						placeholder='Leave blank to write to everyone'
						type='number'
						value={skipRecentlyContactedDays}
					/>
					<FieldDescription>
						{skipDaysInvalid
							? 'Enter a whole number of days greater than zero, or leave this blank.'
							: 'The AI weighs recent contact when deciding whether someone should be left out of this campaign.'}
					</FieldDescription>
				</Field>
			</FieldGroup>

			<QuotaSummary loading={quotaLoading} quota={quota} webSearchEnabled={webSearchEnabled} />
		</div>
	);
};

const QuotaSummary: React.FC<{
	loading: boolean;
	quota?: IAICampaignQuota;
	webSearchEnabled: boolean;
}> = ({ loading, quota, webSearchEnabled }) => {
	if (loading) {
		return (
			<div className={css(styleSheet.quotaCard)}>
				<LoadingSpinner type='small' />
			</div>
		);
	}

	if (!quota) {
		return null;
	}

	const available = availableForBrief(quota, webSearchEnabled) ?? 0;
	const exhausted = available <= 0;

	return (
		<div className={css(styleSheet.quotaCard)}>
			<p className={css(styleSheet.quotaHeadline, exhausted ? styleSheet.quotaExhausted : undefined)}>
				{exhausted
					? 'No AI emails left this month'
					: `You can generate up to ${available.toLocaleString()} emails in this campaign`}
			</p>
			<p className={css(styleSheet.quotaNote)}>
				{`This month: ${quota.totalUsed.toLocaleString()} of ${quota.totalLimit.toLocaleString()} AI emails used`}
				{webSearchEnabled
					? `, including ${quota.webResearchUsed.toLocaleString()} of ${quota.webResearchLimit.toLocaleString()} with web research.`
					: '.'}
				{` Any single campaign is capped at ${quota.perCampaignLimit.toLocaleString()}.`}
			</p>
			{exhausted && webSearchEnabled && quota.availableWithoutWebResearch > 0 && (
				<p className={css(styleSheet.quotaNote)}>
					Turning off web research would let this campaign use the wider allowance instead.
				</p>
			)}
		</div>
	);
};
